/**
 * Frontend helper pre stĺpec Nasledujúci krok.
 * Preferuje locale-aware CRM step catalog; BE *_label fields may be SK/EN baked.
 */
import { displayCrmStepName, parseCrmStateName } from './crmStateI18n';
import { uiT } from './uiTranslate';

const PHASE_LABEL_KEYS = Object.freeze({
  approval: 'Workflow.NextStep.Approval',
  execution: 'Workflow.NextStep.Execution',
  inspection: 'Workflow.NextStep.Inspection',
});

const SPECIAL_LABEL_KEYS = Object.freeze({
  quoteFollowup: 'Workflow.NextStep.QuoteFollowup',
  acquisitionEvaluation: 'Workflow.NextStep.AcquisitionEvaluation',
});

// RAW multilingual BE match lexicon (not UI chrome)
const KNOWN_PHASE_LABELS = Object.freeze([
  'Schválenie', 'Approval', 'Genehmigung',
  'Vykonanie', 'Execution', 'Ausführung',
  'Kontrola', 'Inspection', 'Kontrolle',
]);

const EXECUTION_GENERIC_LABELS = Object.freeze([
  'Vykonanie', 'Execution', 'Ausführung',
]);

// RAW multilingual BE match lexicon (not UI chrome)
const KNOWN_SPECIAL_LABELS = Object.freeze({
  quoteFollowup: ['Dosledovanie', 'Follow-up', 'Nachverfolgung', 'Rozhodnutie', 'Decision', 'Entscheidung', 'KDecision'],
  acquisitionEvaluation: ['Vyhodnotenie', 'Evaluation', 'Auswertung'],
});

function translateKey(key, t) {
  if (typeof t === 'function') {
    const translated = t(key);
    if (translated && translated !== key) return translated;
  }
  return uiT(key);
}

function resolveKnownSpecialLabel(fromResolver, rowData, t) {
  const ticketType = String(rowData?.type ?? '').trim();
  if (KNOWN_SPECIAL_LABELS.quoteFollowup.includes(fromResolver) || (ticketType === 'quote_request' && fromResolver === '')) {
    return translateKey(SPECIAL_LABEL_KEYS.quoteFollowup, t);
  }
  if (KNOWN_SPECIAL_LABELS.acquisitionEvaluation.includes(fromResolver) || (ticketType === 'acquisition' && fromResolver === '')) {
    return translateKey(SPECIAL_LABEL_KEYS.acquisitionEvaluation, t);
  }
  return null;
}

function normalizeMatch(text) {
  return String(text ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

/**
 * Resolve a BE-baked step label (any locale) via crm_steps catalog → current locale.
 * @param {string} rawLabel
 * @param {object[]} crmSteps
 * @param {string} locale
 */
export function localizeCrmStepLabel(rawLabel, crmSteps, locale = 'sk') {
  const needle = normalizeMatch(rawLabel);
  if (!needle || !Array.isArray(crmSteps) || crmSteps.length === 0) return '';
  for (const step of crmSteps) {
    const p = parseCrmStateName(step?.name);
    const variants = [p.sk, p.en, p.de].map(normalizeMatch).filter(Boolean);
    if (variants.includes(needle)) {
      return displayCrmStepName(step.name, locale);
    }
  }
  return '';
}

function stepFromRow(rowData, crmSteps) {
  if (!Array.isArray(crmSteps) || crmSteps.length === 0) return null;
  const stepId = Number(rowData?.execution_crm_step_id ?? rowData?.crm_step_id ?? 0);
  if (stepId > 0) {
    return crmSteps.find((s) => Number(s.id) === stepId) || null;
  }
  return null;
}

/**
 * @param {object} rowData
 * @param {function} t
 * @param {{ locale?: string, crmSteps?: object[] }} [opts]
 */
export function resolveWorkflowNextStepListLabel(rowData, t, opts = {}) {
  const locale = ['sk', 'en', 'de'].includes(String(opts.locale)) ? String(opts.locale) : 'sk';
  const crmSteps = Array.isArray(opts.crmSteps) ? opts.crmSteps : [];
  const phase = String(rowData?.workflow_next_step_phase ?? '').trim();
  const fromResolver = String(rowData?.workflow_next_step_label ?? '').trim();
  const stepLabel = String(rowData?.execution_crm_step_label ?? '').trim();

  const catalogStep = stepFromRow(rowData, crmSteps);
  const fromCatalogId = catalogStep ? displayCrmStepName(catalogStep.name, locale) : '';

  if (phase === 'inspection') {
    const specialLabel = resolveKnownSpecialLabel(fromResolver, rowData, t);
    if (specialLabel) return specialLabel;
  }

  if (
    (stepLabel || fromCatalogId)
    && (phase === 'execution' || phase === '')
    && (!fromResolver || EXECUTION_GENERIC_LABELS.includes(fromResolver))
  ) {
    if (fromCatalogId) return fromCatalogId;
    const localized = localizeCrmStepLabel(stepLabel, crmSteps, locale);
    return localized || stepLabel;
  }

  if (phase && PHASE_LABEL_KEYS[phase] && (!fromResolver || KNOWN_PHASE_LABELS.includes(fromResolver))) {
    return translateKey(PHASE_LABEL_KEYS[phase], t);
  }

  if (fromCatalogId && (!fromResolver || EXECUTION_GENERIC_LABELS.includes(fromResolver) || KNOWN_PHASE_LABELS.includes(fromResolver))) {
    return fromCatalogId;
  }

  if (fromResolver) {
    const localized = localizeCrmStepLabel(fromResolver, crmSteps, locale);
    if (localized) return localized;
    if (fromCatalogId) return fromCatalogId;
    return fromResolver;
  }
  if (stepLabel) {
    const localized = localizeCrmStepLabel(stepLabel, crmSteps, locale);
    return localized || stepLabel;
  }
  if (fromCatalogId) return fromCatalogId;
  if (phase && PHASE_LABEL_KEYS[phase]) {
    return translateKey(PHASE_LABEL_KEYS[phase], t);
  }
  return '';
}

export const WORKFLOW_NEXT_STEP_PHASES = Object.freeze(['approval', 'execution', 'inspection']);
